import React, { useEffect, useState } from "react";
import ReactDatePicker from "react-datepicker";
import Loader from "react-spinner-loader";
import moment from "moment";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import { useTranslation } from "react-i18next";
import "react-datepicker/dist/react-datepicker.css";
import HumanDashboardCard from "./DashboardCard/HumanDashboardCard";
import HumanLossDougnut from "./DashboardCard/HumanLossTableDougnut";
import FamilyAffectedTrendViewHumanLoss from "./DashboardCard/FamilyAffectedTrendViewHumanLoss";
import { dashboardCardConfigHumanLoss } from "./HumanLossDamageConfig";
import "./HumanLossDashboard.css";

const HumanLossDashboard = () => {
  const handle = useFullScreenHandle();
  const { t } = useTranslation();

  const [clickedKey, setClickedKey] = useState("");
  const [loader, setLoader] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(2026);
  const [selectedIncident, setSelectedIncident] = useState("");
  const [applyClicked, setApplyClicked] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);

  const [fromDate, setFromDate] = useState(new Date(2026, 0, 1));
  const [toDate, setToDate] = useState(new Date());
  const [appliedFromDate, setAppliedFromDate] = useState(new Date(2026, 0, 1));
  const [appliedToDate, setAppliedToDate] = useState(new Date());

  const fromDateNew = appliedFromDate ? moment(appliedFromDate).format("YYYY-MM-DD") : "";
  const toDateNew = appliedToDate ? moment(appliedToDate).format("YYYY-MM-DD") : "";

  useEffect(() => {
    if (fromDate && toDate && fromDate > toDate) setError("From date must be before To date.");
    else if (!fromDate || !toDate) setError(null);
    else setError(null);
  }, [fromDate, toDate]);

  const toggleFullscreen = () => { isFullscreen ? handle.exit() : handle.enter(); setIsFullscreen(!isFullscreen); };

  const handleYearChange = (e) => {
    const year = parseInt(e.target.value);
    setSelectedYear(year);
    const today = new Date();
    const newFrom = new Date(year, 0, 1);
    const yearEnd = new Date(year, 11, 31);
    setFromDate(newFrom);
    setToDate(today <= yearEnd ? today : yearEnd);
  };

  const handleApply = () => {
    if (!fromDate || !toDate) { setError("Please select both dates"); return; }
    if (fromDate > toDate) { setError("From date cannot be after To date"); return; }
    setError(null);
    setAppliedFromDate(fromDate);
    setAppliedToDate(toDate);
    setApplyClicked(true);
    setFirstLoad(false);
  };
  const ReadOnlyDateInput = React.forwardRef((props, ref) => (
  <input {...props} ref={ref} onKeyDown={e => e.preventDefault()} readOnly />
));

  const onApplyHandled = () => { setApplyClicked(false); };

  const handleIncidentSelection = (incidentId, incidentName) => {
    setSelectedIncident({ id: incidentId, name: incidentName });
  };

  const isApplyDisabled = !!error || !fromDate || !toDate;

  return (
    <FullScreen handle={handle}>
      <div className={`hld-root${isFullscreen ? " hld-fs" : ""}`}>
        <Loader show={loader} type="body" stack="vertical" message="Loading Data" />

        {/* ── NAV ── */}
        <nav className="hld-nav">
          <div className="hld-nav-brand">
            <div className="hld-nav-logo">
              <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
                <circle cx="10" cy="6.5" r="2.8" stroke="white" strokeWidth="1.5" opacity=".9"/>
                <path d="M4.5 18c0-3 2.5-5.5 5.5-5.5s5.5 2.5 5.5 5.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity=".9"/>
              </svg>
            </div>
            <div className="hld-nav-title">Primary Incidents Report (Human Loss)</div>
          </div>

          <div className="hld-nav-right">
            {/* Year */}
            <div className="hld-nav-group">
              <span className="hld-nav-label">Year</span>
              <select className="hld-nav-select" value={selectedYear} onChange={handleYearChange}>
                <option value={2024}>2024</option>
                <option value={2025}>2025</option>
                <option value={2026}>2026</option>
              </select>
            </div>

            {/* Date range */}
            <div className="hld-daterange">
              <div className="hld-daterange-field">
                <span className="hld-dr-label">FROM</span>
                <ReactDatePicker selected={fromDate} onChange={setFromDate}
                  dateFormat="dd/MM/yyyy" isClearable placeholderText="Select" todayButton="Today"
                  className="hld-dp-input" minDate={new Date(selectedYear, 0, 1)} maxDate={new Date(selectedYear, 11, 31)} customInput={<ReadOnlyDateInput />}/>
              </div>
              <div className="hld-dr-divider" />
              <div className="hld-daterange-field">
                <span className="hld-dr-label">TO</span>
                <ReactDatePicker selected={toDate} onChange={setToDate}
                  dateFormat="dd/MM/yyyy" isClearable placeholderText="Select" todayButton="Today"
                  className="hld-dp-input" minDate={new Date(selectedYear, 0, 1)} maxDate={new Date(selectedYear, 11, 31)} customInput={<ReadOnlyDateInput />}/>
              </div>
            </div>

            <button className="hld-btn-apply" onClick={handleApply} disabled={isApplyDisabled}>Apply</button>
            <div className="hld-nav-divider" />
            <button className="hld-btn-icon" onClick={toggleFullscreen}>
              {isFullscreen ? (
                <svg viewBox="0 0 18 18" fill="none" width="14" height="14"><path d="M2 6H6V2M16 6H12V2M2 12H6V16M16 12H12V16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
              ) : (
                <svg viewBox="0 0 18 18" fill="none" width="14" height="14"><path d="M2 6V2h4M12 2h4v4M2 12v4h4M16 12v4h-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
              )}
            </button>
          </div>
        </nav>

        {error && (
          <div className="hld-error-strip">
            <svg viewBox="0 0 16 16" fill="none" width="13" height="13">
              <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M8 5v3.5M8 10.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            {error}
          </div>
        )}

        <div className="hld-content">
          {/* ── CARDS ── */}
          {dashboardCardConfigHumanLoss?.map((card, index) => (
            <HumanDashboardCard
              key={index}
              config={card}
              clickedKey={clickedKey}
              setClickedKey={setClickedKey}
              onIncidentSelect={handleIncidentSelection}
              fromDateNew={fromDateNew}
              toDateNew={toDateNew}
              applyClicked={applyClicked}
              onApplyHandled={onApplyHandled}
              firstLoad={firstLoad}
              setLoader={setLoader}
            />
          ))}

          {/* ── CHARTS ROW ── */}
          <div className="hld-grid-2">
            <HumanLossDougnut
              fromDateNew={fromDateNew} toDateNew={toDateNew}
              applyClicked={applyClicked} onApplyHandled={onApplyHandled}
              firstLoad={firstLoad} title={selectedIncident.name}
              selectedIncident={selectedIncident}
            />
            <FamilyAffectedTrendViewHumanLoss
              title={selectedIncident.name}
              activeTab="dss"
              fromDateNew={fromDateNew} toDateNew={toDateNew}
              selectedIncident={selectedIncident}
              applyClicked={applyClicked} onApplyHandled={onApplyHandled}
              firstLoad={firstLoad} setLoader={setLoader}
            />
          </div>
        </div>
      </div>
    </FullScreen>
  );
};
export default HumanLossDashboard;